/**
 * bpa-brief.ts — Editorial copy layer for the Best Practice Analyzer page
 * standfirst. Mirrors `posture-brief.ts` exactly in shape and voice rules.
 * The BPA pillar tells a slightly different story: the subject is "Best
 * Practice", the named risk is the weakest starred framework or the top
 * SHALL violation, and the verdict bands key off the BPA composite
 * (`bpaScore`) rather than the Guardian composite.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (score, delta, weakest framework, top action, open SHALL
 * count), never from picking among synonymous phrasings of the same fact.
 *
 * Verdict precedence at score >= 90 (non-negative): a known open count of 1
 * or more wins first (named singular/plural), then an open topAction (so
 * the verdict never says "Routine watch only" beside a headline that
 * names a specific action to take), then a known zero -> routine watch,
 * then unknown -> keep the cadence.
 *
 * Loading-state handling: when `input.bpaScore` is null/undefined we return
 * a neutral "Reading practice…" brief rather than synthesizing a worst-band
 * verdict. Without this guard the composer would coerce `null → 0`, which
 * lands the loading state in the lowest editorial band and shows the user
 * an authoritative-but-wrong verdict for a fraction of a second before the
 * real data arrives. Note: `bpaScore === 0` is a legitimate real value
 * (e.g. a brand-new tenant with no starred frameworks resolved) and still
 * routes through the normal band logic — only `null`/`undefined` triggers
 * the loading brief.
 *
 * Unknown vs zero: `openShallCount` may be `undefined` (unknown — the
 * violations query hasn't resolved yet) as distinct from a known `0`. An
 * unknown count must never be treated as zero and must never produce
 * all-clear copy; it routes to the routine/cadence fallbacks instead.
 *
 * Copy says "critical control", never "SHALL" (D33); brief-copy-lint L5 bans
 * the keyword in the rendered headline, lede and verdict.
 *
 * Action-first fields (blueprint §6.2 row 5): `headline` names the ranker's
 * top action first, then the laggard framework, then the critical count;
 * `lede` names the failing controls from `openCount` with the critical share
 * from `openShallCount`, falling back to the critical count alone, then the
 * score. The ranker's titles are verb-led ("Enforce …", from
 * `policy_actions.json` via BpaActionMapper), so the title headline reads
 * "{Title} first." rather than wrapping a verb in "Start with …".
 */

import { briefProvenance, cap, controlPrimaryLabel, controlsLedeBody, knownCount, LEDE_FRAGMENTS } from '@/lib/brief-helpers'
import type { ActionBriefFields } from '@/lib/brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface BpaBrief {
  /** Six words or fewer. Not drawn: the Carbon pillar brief shows no verdict (D22). */
  verdict: string
  /** The provenance line (rule 4, D36): "Assessed by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  /** Score delta vs N days ago, for PillarBrief's delta tag on the score tile. `null` when unknown. */
  delta: { value: number; days: number } | null
  /** Action-first h1 (blueprint §6.2 row 5). */
  headline: string
  /** The framework the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
  /** The view's one Soft Green button; only with a `primaryHref` and open work to review. */
  primaryAction?: ActionBriefFields['primaryAction']
}

export interface BpaFrameworkRow {
  /** Framework code, e.g. "SCuBA", "CIS", "EIDSCA", "MCSB". */
  code: string
  /** 0-100 framework score (weighted pass-rate). */
  score: number
  /** True when `includeInGuardianScore` is set. */
  starred: boolean
  /** True for MCSB — the always-included Microsoft Cloud Security Benchmark. */
  isAlwaysIncluded: boolean
}

export interface BpaTopAction {
  /** Imperative-friendly title, e.g. "Enforce conditional access for admin roles". */
  title: string
  /** Framework citation code, e.g. "SCuBA AAD.4.1v1". */
  framework?: string
}

export interface BpaWeakestFramework {
  /** Framework code (used inside the six-word verdict cap). */
  code: string
  /** 0-100 framework score. */
  score: number
}

export interface BpaBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** When Guardian last evaluated Best Practice (`lastEvaluatedAt`): null when
   *  never, undefined while unknown. Feeds the provenance line. */
  assessedAt?: string | null
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is deterministic rather than seeded per tenant per day. */
  tenantId?: string
  /** Composite BPA score (starred-frameworks weighted average), 0-100. */
  bpaScore?: number | null
  /** Per-framework rows. May be empty; the composer does not read them. */
  frameworks?: BpaFrameworkRow[]
  /** Open SHALL violation count, score-driving frameworks only. Unknown
   *  (`undefined`) when the violations query hasn't resolved yet. */
  openShallCount?: number
  /**
   * Open violations in the same starred scope (`useViolationSummary` ->
   * `open`, surfaced as `useComplianceDashboard().dashboardData
   * .totalViolations`). Drives the lede "{f} controls are failing";
   * `openShallCount` stays the critical share. `undefined` while loading.
   */
  openCount?: number
  /** Top-ranked SHALL across starred frameworks (PriorityAction[0]). */
  topAction?: BpaTopAction
  /** Where the primary button goes (the composer owns no routes). Without it,
   *  or on an all-clear or calm headline, the brief has no `primaryAction`. */
  primaryHref?: string
  /** Weakest starred framework — the one dragging the composite. */
  weakestFramework?: BpaWeakestFramework
  /** 30-day delta on the BPA composite. */
  delta30d?: { score?: number; days?: number } | null
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** Strip trailing sentence punctuation from an action title before it's
 *  re-embedded in a new sentence (avoids "Enforce MFA.. first."). */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Word form for 1-9, digits from 10 up — used by the routine-watch verdict's
 *  still-open SHALL count so it reads as prose. */
function countWord1to9(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  STILL_ONE: 'One critical control still open.',
  STILL_FEW: '{n} critical controls still open.',
  CADENCE: 'Keep the review cadence.',
  PULLING_DOWN: '{code} is pulling the score down.',
  SCORE_DOWN: 'Score down {n} this month.',
  LIFT_NEXT: 'Lift {code} next.',
  LEAD_NEXT: 'Take the lead item next.',
  LIFT_FIRST: 'Lift {code} first.',
  START_LEAD: 'Start with the lead item.',
  NEEDS_WORK: '{code} needs the most work.',
  WORK_QUEUE: 'Work the queue top down.',
}

/**
 * Action-first h1 (blueprint §6.2 row 5): top action, laggard, critical count.
 * The all-clear and cadence lines use the verdict's own all-clear band
 * (90 and up, not falling), so the h1 never says "Nothing to close" beside a
 * verdict that names a lead item.
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading Best Practice.',
  TOP_ACTION: '{title} first.',
  LAGGARD: 'Start with {code} this week.',
  CRIT_ONE: 'Close the critical control first.',
  CRIT_FEW: 'Close the {n} critical controls first.',
  CLEAR: 'Nothing to close this week.',
  CADENCE: 'Keep the usual review cadence this week.',
  QUEUE: 'Work the queue top down this week.',
}

/** The primary button: the top control, else the failing-controls count. */
const PRIMARY_ACTION = {
  FIX: 'Fix {title}',
  FAILING_ONE: 'Review the failing control',
  FAILING_FEW: 'Review the {n} failing controls',
  FAILING_ANY: 'Review the failing controls',
}

/** The lede: the failing and critical controls (`LEDE_FRAGMENTS`), else the score. */
const LEDE = {
  LOADING: '**Guardian is reading your frameworks**.',
  SCORE: '**Best Practice is at {score}**.',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function pickVerdict(
  score: number,
  weakest: BpaWeakestFramework | undefined,
  laggard: boolean,
  openShall: number | undefined,
  negative: boolean,
  n: string,
  hasTopAction: boolean,
): string {
  const code = weakest ? weakest.code : ''

  if (score >= 90 && !negative) {
    if (openShall === 1) return VERDICT.STILL_ONE
    if (openShall !== undefined && openShall >= 2) {
      return VERDICT.STILL_FEW.replace('{n}', countWord1to9(openShall))
    }
    if (laggard) return VERDICT.LIFT_NEXT.replace('{code}', code)
    if (hasTopAction) return VERDICT.LEAD_NEXT
    if (openShall === 0) return VERDICT.ROUTINE_WATCH
    return VERDICT.CADENCE
  }

  if (score >= 75) {
    if (negative) {
      if (laggard) return VERDICT.PULLING_DOWN.replace('{code}', code)
      return VERDICT.SCORE_DOWN.replace('{n}', n)
    }
    if (laggard) return VERDICT.LIFT_NEXT.replace('{code}', code)
    return VERDICT.LEAD_NEXT
  }

  if (score >= 60) {
    if (laggard) return VERDICT.LIFT_FIRST.replace('{code}', code)
    return VERDICT.START_LEAD
  }

  if (laggard) return VERDICT.NEEDS_WORK.replace('{code}', code)
  return VERDICT.WORK_QUEUE
}

/* -------------------------------------------------------------------------- */
/* Action-first headline (blueprint §6.2 row 5)                               */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  topAction: BpaTopAction | undefined,
  laggard: boolean,
  weakest: BpaWeakestFramework | undefined,
  openShall: number | undefined,
  score: number,
  negative: boolean,
  openCount: number | undefined,
): { headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  if (topAction?.title) {
    return { headline: HEADLINE_ACTION.TOP_ACTION.replace('{title}', cap(stripTrailingPunct(topAction.title))) }
  }
  if (laggard && weakest) {
    return { headline: HEADLINE_ACTION.LAGGARD.replace('{code}', weakest.code), emphasis: { text: weakest.code } }
  }
  if (openShall === 1) return { headline: HEADLINE_ACTION.CRIT_ONE }
  if (openShall !== undefined && openShall >= 2) {
    return { headline: HEADLINE_ACTION.CRIT_FEW.replace('{n}', String(openShall)) }
  }
  // No all-clear beside a lede that names failing controls (blueprint §6.4 L6).
  const failing = knownCount(openCount)
  if (openShall === 0 && score >= 90 && !failing) return { headline: HEADLINE_ACTION.CLEAR }
  return { headline: score >= 90 && !negative ? HEADLINE_ACTION.CADENCE : HEADLINE_ACTION.QUEUE }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a BpaBrief from the active BPA pillar state. All inputs are
 * optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.bpaScore` is null/undefined we return a neutral "Reading
 * practice…" brief; see the file-header docblock for rationale.
 */
export function composeBpaBrief(input: BpaBriefInput = {}): BpaBrief {
  const byline = briefProvenance(input.assessedAt, { now: input.now })

  if (input.bpaScore == null) {
    return {
      verdict: 'Reading practice…',
      byline,
      delta: null,
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.bpaScore
  const weakest = input.weakestFramework
  const laggard = !!(weakest && weakest.score < 70)
  const openShall = input.openShallCount

  const deltaValue = typeof input.delta30d?.score === 'number' ? input.delta30d.score : undefined
  const deltaDays = typeof input.delta30d?.days === 'number' ? input.delta30d.days : 30
  const negative = typeof deltaValue === 'number' && deltaValue < 0
  const n = deltaValue === undefined ? '' : formatDelta(Math.abs(deltaValue))

  const verdict = pickVerdict(score, weakest, laggard, openShall, negative, n, !!input.topAction?.title)

  const action = pickActionHeadline(input.topAction, laggard, weakest, openShall, score, negative, input.openCount)
  const controls = controlsLedeBody(input.openCount, openShall)
  const lede = controls ? `${controls}.` : LEDE.SCORE.replace('{score}', String(Math.round(score)))
  const href = input.primaryHref?.trim()
  const calm = action.headline === HEADLINE_ACTION.CLEAR || action.headline === HEADLINE_ACTION.CADENCE

  return {
    verdict,
    byline,
    delta: deltaValue === undefined ? null : { value: deltaValue, days: deltaDays },
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
    ...(href && !calm
      ? {
          primaryAction: {
            label: controlPrimaryLabel(PRIMARY_ACTION, input.topAction?.title, input.openCount),
            href,
          },
        }
      : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  PRIMARY_ACTION,
  LEDE,
  LEDE_FRAGMENTS,
}
